import * as React from 'react';
import { UIText2 } from '@cian/ui-kit/typography';
// eslint-disable-next-line import/no-extraneous-dependencies
import { clsx } from 'clsx';

import * as styles from './GeoSuggest.css';
import { calculateScrollOffset } from './utils';
import { IRegion } from '../../../../types/requestForm';

export interface IGeoSuggestProps {
  onSelect(region: IRegion): void;
  suggests: IRegion[];
}

export interface IGeoSuggestRef {
  selectActive(): void;
  setActiveNext(): void;
  setActivePrev(): void;
}

export const GeoSuggest = React.forwardRef<IGeoSuggestRef, IGeoSuggestProps>(({ suggests, onSelect }, ref) => {
  const [activeIndex, setActiveIndex] = React.useState(-1);

  const containerRef = React.useRef<HTMLUListElement>(null);
  const activeItemRef = React.useRef<HTMLLIElement>(null);
  const suggestsCount = suggests.length;

  const handleItemClick = React.useCallback(
    (event: React.MouseEvent) => {
      const index = event.currentTarget.getAttribute('data-index') as string;
      const region = suggests[+index];
      onSelect(region);
    },
    [onSelect, suggests],
  );

  const handleItemHover = React.useCallback((event: React.MouseEvent) => {
    const index = event.currentTarget.getAttribute('data-index') as string;
    setActiveIndex(+index);
  }, []);

  const setPrevActiveIndex = React.useCallback(() => {
    setActiveIndex(prev => (prev <= 0 ? suggestsCount - 1 : prev - 1));
  }, [suggestsCount]);

  const setNextActiveIndex = React.useCallback(() => {
    setActiveIndex(prev => (prev >= suggestsCount - 1 ? 0 : prev + 1));
  }, [suggestsCount]);

  const selectActiveSuggest = React.useCallback(() => {
    const region = suggests[activeIndex] || suggests[0];
    if (region) {
      onSelect(region);
    }
  }, [onSelect, activeIndex, suggests]);

  React.useImperativeHandle(ref, () => ({
    selectActive: selectActiveSuggest,
    setActiveNext: setNextActiveIndex,
    setActivePrev: setPrevActiveIndex,
  }));

  React.useEffect(() => {
    const container = containerRef.current;
    const activeItem = activeItemRef.current;

    if (container && activeItem) {
      container.scrollTop = calculateScrollOffset(activeItem, container);
    }
  }, [activeIndex]);

  return (
    <ul ref={containerRef} className={styles['suggests']} role="listbox">
      {suggests.map((region, idx) => {
        const isActive = idx === activeIndex;

        return (
          <li
            ref={isActive ? activeItemRef : undefined}
            key={region.id}
            className={clsx(styles['suggest'], isActive && styles['suggest--active'])}
            data-index={idx}
            onClick={handleItemClick}
            onMouseEnter={handleItemHover}
            role="option"
          >
            <UIText2>{region.label}</UIText2>
          </li>
        );
      })}
    </ul>
  );
});

GeoSuggest.displayName = 'GeoSuggest';
